/**
 * 범용 네이티브 사진첩 브릿지 (앱 전용)
 *   - Android : window.mung.openPhotoPicker(requestId, paramsJson)  (함수 존재로 신버전 감지)
 *   - iOS     : window.webkit.messageHandlers.mung.postMessage(["openPhotoPicker", requestId, paramsJson])
 *               (핸들러는 구버전에도 있어 명령 지원을 감지 불가 → 앱 버전으로 게이팅)
 *   - 결과 콜백: window.onPhotoPicked(requestId, jsonArrayString)
 *
 * paramsJson: { petSeq?, uploadPath?, multiSelect? }
 *   - petSeq 있으면 [스마트폰]+[클라우드](그 펫), 없으면 [스마트폰]만
 * 반환: [{ fileSeq, fileUrl }, ...] (클라우드 선택 + 스마트폰 업로드 통합)
 *
 * ⚠️ 정책: 앱 버전 1.0.0 미만은 기존 웹 모달 경로 유지, 1.0.0 이상만 네이티브 통합 피커 사용.
 *    네이티브 핸들러가 포함된 첫 배포 빌드는 반드시 버전을 1.0.0 이상으로 올려야 켜진다.
 */
import { isMungAppAndroid, isMungAppIOS } from "./externalBrowser";
import { parseNativeAppInfo } from "./appVersion";

export interface PickedPhoto {
  fileSeq: number;
  fileUrl: string;
}

interface Pending {
  resolve: (photos: PickedPhoto[]) => void;
  reject: (err: Error) => void;
}

const pendingMap: Record<string, Pending> = {};
let callbackInstalled = false;

/**
 * 범용 사진첩 브릿지가 포함된 "최소 앱 버전".
 *  정책: 앱 버전 1.0.0 미만은 기존 웹 모달 경로 유지, 1.0.0 이상만 네이티브 피커 사용.
 *  iOS/Android 공통(안드는 함수존재 AND 버전으로 이중 확인).
 */
const MIN_PICKER_APP_VERSION: { android: string; ios: string } = {
  android: "1.0.0",
  ios: "1.0.0",
};

/** semver 비교: v >= min 이면 true */
const isVersionGte = (v: string | null, min: string): boolean => {
  if (!v) return false;
  const a = v.split(".").map((n) => parseInt(n, 10) || 0);
  const b = min.split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const x = a[i] || 0;
    const y = b[i] || 0;
    if (x !== y) return x > y;
  }
  return true;
};

function ensureCallback() {
  if (callbackInstalled) return;
  callbackInstalled = true;
  (window as unknown as Record<string, unknown>).onPhotoPicked = (
    requestId: string,
    json: string,
  ) => {
    const pending = pendingMap[requestId];
    if (!pending) return;
    delete pendingMap[requestId];
    try {
      const arr = JSON.parse(json || "[]");
      pending.resolve(Array.isArray(arr) ? (arr as PickedPhoto[]) : []);
    } catch {
      pending.resolve([]);
    }
  };
}

/**
 * 앱에서 네이티브 사진첩 브릿지 사용 가능 여부.
 *  - Android : window.mung.openPhotoPicker 함수 존재로 정확히 감지(구버전 앱엔 없음 → 자동으로 기존 경로).
 *  - iOS     : mung 핸들러는 구버전에도 있어 명령 지원을 감지 불가 → 앱 버전(UA)으로 게이팅.
 *  → 검수/점진배포 중 기존 사용자(구버전)는 항상 false 가 되어 기존 웹 모달 경로로 안전하게 동작.
 */
export const canUseNativePhotoPicker = (): boolean => {
  const w = window as any;
  // 정책 통일: UA(MT_I/MT_A)에서 올라온 앱 버전으로 게이팅. 1.0.0 미만은 기존 경로.
  const info = parseNativeAppInfo();
  if (isMungAppAndroid()) {
    return (
      typeof w.mung?.openPhotoPicker === "function" &&
      isVersionGte(info.version, MIN_PICKER_APP_VERSION.android)
    );
  }
  if (isMungAppIOS()) {
    const hasHandler =
      typeof w.webkit?.messageHandlers?.mung?.postMessage === "function";
    return hasHandler && isVersionGte(info.version, MIN_PICKER_APP_VERSION.ios);
  }
  return false;
};

export interface NativePhotoPickerOptions {
  /** 지정 시 클라우드 탭이 그 펫 스코프로 노출. 미지정이면 스마트폰만 */
  petSeq?: number;
  /** 스마트폰 선택분 업로드 경로 (기본: 사진첩 업로드) */
  uploadPath?: string;
  /** 최대 선택 수 (기본 10) */
  multiSelect?: number;
  /**
   * 스마트폰 선택분을 클라우드 사진첩(INFO_PHOTO_ALBUM)에도 저장할지.
   * 기본 false = 알림장 첨부 전용(사진첩에 안 올라감, 유료 저장용량 미차감).
   * true = 사진첩에도 저장(향후 "사진첩에도 저장" 옵트인용).
   */
  saveToAlbum?: boolean;
}

/** 네이티브 사진첩을 열고 선택 결과(fileSeq 목록)를 받는다. 취소 시 빈 배열. */
export const openNativePhotoPicker = (
  opts: NativePhotoPickerOptions = {},
  // 대량 선택(수백 장) 시 순차 업로드가 길어질 수 있어 넉넉히(15분)
  timeoutMs = 900000,
): Promise<PickedPhoto[]> => {
  ensureCallback();
  return new Promise<PickedPhoto[]>((resolve, reject) => {
    const requestId = `pp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const timer = setTimeout(() => {
      if (pendingMap[requestId]) {
        delete pendingMap[requestId];
        reject(new Error("사진첩 응답 시간이 초과되었습니다."));
      }
    }, timeoutMs);
    pendingMap[requestId] = {
      resolve: (r) => {
        clearTimeout(timer);
        resolve(r);
      },
      reject: (e) => {
        clearTimeout(timer);
        reject(e);
      },
    };

    const params = JSON.stringify({
      petSeq: opts.petSeq,
      uploadPath: opts.uploadPath ?? "/api/business/photo/upload-image",
      multiSelect: opts.multiSelect ?? 10,
      // 기본 false: 알림장 첨부 전용(클라우드 사진첩에 저장하지 않음)
      saveToAlbum: opts.saveToAlbum ?? false,
    });

    const w = window as any;
    try {
      if (isMungAppAndroid() && typeof w.mung?.openPhotoPicker === "function") {
        w.mung.openPhotoPicker(requestId, params);
      } else if (
        isMungAppIOS() &&
        typeof w.webkit?.messageHandlers?.mung?.postMessage === "function"
      ) {
        w.webkit.messageHandlers.mung.postMessage([
          "openPhotoPicker",
          requestId,
          params,
        ]);
      } else {
        delete pendingMap[requestId];
        clearTimeout(timer);
        reject(new Error("네이티브 사진첩을 사용할 수 없습니다."));
      }
    } catch (e) {
      delete pendingMap[requestId];
      clearTimeout(timer);
      reject(e instanceof Error ? e : new Error("브릿지 호출 실패"));
    }
  });
};
